import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { UiMessages } from './messages/en';

/*
 * Shared i18n core for the Bounded web apps (D-031: English first). Messages are typed modules:
 * this package owns the strings its components use (UiMessages); each app merges its own on top
 * and types its other locales against the result, so a missing translation fails typecheck.
 * Functions carry parameters, plurals and inline markup, so there is no runtime message parser.
 *
 * Never translated: anything a device wrote (commands, summaries, reason codes it reports).
 */
export const LOCALES = [
  { id: 'en', name: 'English' },
  { id: 'zh-CN', name: '简体中文' },
] as const;
export type Locale = (typeof LOCALES)[number]['id'];
const STORAGE_KEY = 'bounded.locale';

const isLocale = (v: unknown): v is Locale => LOCALES.some((l) => l.id === v);
/** Saved choice, then the browser's languages (zh-* → zh-CN), then English. */
export function detectLocale(): Locale {
  try { const saved = localStorage.getItem(STORAGE_KEY); if (isLocale(saved)) return saved; } catch { /* storage blocked */ }
  for (const tag of navigator.languages ?? [navigator.language]) {
    if (isLocale(tag)) return tag;
    if (tag.toLowerCase().startsWith('zh')) return 'zh-CN';
    if (tag.toLowerCase().startsWith('en')) return 'en';
  }
  return 'en';
}

type Ctx = { locale: Locale; t: UiMessages; setLocale: (l: Locale) => Promise<void> };
const I18n = createContext<Ctx | null>(null);

/** `load` returns the app's full message set (UI messages merged with the app's own) for a locale. */
export function I18nProvider<M extends UiMessages>({ initial, messages, load, children }: { initial: Locale; messages: M; load: (l: Locale) => Promise<M>; children: ReactNode }) {
  const [state, setState] = useState<{ locale: Locale; t: M }>({ locale: initial, t: messages });
  const setLocale = useCallback(async (locale: Locale) => {
    const t = await load(locale);
    try { localStorage.setItem(STORAGE_KEY, locale); } catch { /* storage blocked */ }
    document.documentElement.lang = locale;
    setState({ locale, t });
  }, [load]);
  const value = useMemo(() => ({ ...state, setLocale }), [state, setLocale]);
  return <I18n.Provider value={value}>{children}</I18n.Provider>;
}

function useCtx() {
  const ctx = useContext(I18n);
  if (!ctx) throw new Error('I18nProvider missing');
  return ctx;
}
/** The app's full message set; apps wrap this with their own type (`useMessages<AppMessages>()`). */
export const useMessages = <M extends UiMessages = UiMessages>() => useCtx().t as M;
/** UI-package messages only, for shared components. */
export const useT = () => useCtx().t;
export const useLocale = () => { const { locale, setLocale } = useCtx(); return { locale, setLocale }; };

/** Locale-aware formatting. Relative time, dates and lists use Intl; durations use the locale's messages. */
export function useFormat() {
  const { locale, t } = useCtx();
  return useMemo(() => {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
    const dtf = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'medium' });
    const toMs = (v: number | string) => typeof v === 'number' ? v : Date.parse(v);
    return {
      ago(value: number | string, now = Date.now()) {
        const s = Math.max(0, Math.round((now - toMs(value)) / 1_000));
        if (s < 5) return t.time.justNow;
        if (s < 60) return rtf.format(-s, 'second');
        if (s < 3_600) return rtf.format(-Math.floor(s / 60), 'minute');
        if (s < 86_400) return rtf.format(-Math.floor(s / 3_600), 'hour');
        return rtf.format(-Math.floor(s / 86_400), 'day');
      },
      /** `precise` keeps seconds (countdowns); otherwise whole minutes read better for configured windows. */
      duration(seconds: number, precise = true) {
        const s = Math.max(0, Math.round(seconds));
        if (s < 60) return t.time.seconds(s);
        if (s < 3_600) return precise ? t.time.minutesSeconds(Math.floor(s / 60), s % 60) : t.time.minutes(Math.floor(s / 60));
        const h = Math.floor(s / 3_600), m = Math.floor((s % 3_600) / 60);
        return m ? t.time.hoursMinutes(h, m) : t.time.hours(h);
      },
      dateTime: (value: number | string) => dtf.format(new Date(toMs(value))),
      list: (items: string[]) => new Intl.ListFormat(locale, { type: 'conjunction' }).format(items),
      number: (n: number) => new Intl.NumberFormat(locale).format(n),
    };
  }, [locale, t]);
}

/**
 * Pick server-shipped translated text: exact locale, then same language, then the untranslated default.
 * Used for preset metadata and policy parameter labels; never for device-written text.
 */
export function pickLocalized<T extends object>(base: T, i18n: Record<string, Partial<T>> | undefined, locale: Locale): T {
  if (!i18n) return base;
  const exact = i18n[locale];
  const lang = locale.split('-')[0];
  const sameLanguage = exact ?? Object.entries(i18n).find(([k]) => k.split('-')[0] === lang)?.[1];
  return sameLanguage ? { ...base, ...sameLanguage } : base;
}
